/* eslint-disable monorepo/no-internal-import */
import {parseRequestProperties} from '@aboutyou/bootstrap/src/parseRequestProperties';
import {logTransaction} from '@aboutyou/bootstrap/src/utils/logTransaction';
import {getGrpcConfig} from '@aboutyou/config';
import {useStandaloneGrpcRequest} from '@aboutyou/data-fetcher';
import {PaymentService_GetPOAURLLazy} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/payment/service.grpc.lazy';
import {DEVICE_PARAM_TYPE} from '@aboutyou/logic-components/src/features/Checkout/constants';
import {Log} from '@aboutyou/mlm';
import {TADARIDA_USER_PERSIST_KEY} from '@aboutyou/user';
import type {NextFunction, Request, Response} from 'express';
import React from 'react';
import {renderToString} from 'react-dom/server';
import {logPoaProxyStatus} from '../bnplServerLogging';
import {
  AppLinksErrorUI,
  PayOrderAppAppLinksProxyError,
} from './app-poa-error-utils';
import {
  HOP_BY_HOP_HEADERS,
  ProxyConfig,
  checkLoginNeeded,
  getHashToSessionIdHeader,
  getProxyRequestProperties,
  getSessionOrHash,
  resolveAppLinksProxyConfig,
  resolveWebProxyConfig,
} from './utils';

export const PAY_ORDER_WEB_ROUTE = '/a/orders/pay-now';
const PAY_ORDER_API_ROUTE = '/a/orders/pay-now/api';

const createPayOrderProxy =
  (isApiProxy: boolean) =>
  // eslint-disable-next-line complexity
  async (req: Request, res: Response, next: NextFunction) => {
    let proxyConfig: ProxyConfig | null = null;

    try {
      const {
        traceId,
        locale,
        hostname,
        serverCookies,
        isAppLinksSubdomainRequest,
      } = parseRequestProperties({
        req,
        cookieConfig: {
          whitelistedCookies: [TADARIDA_USER_PERSIST_KEY],
        },
      });

      const enhanceTransaction = logTransaction({
        traceId,
        transactionName: 'poa_proxy',
        locale,
        hostname,
        url: req.originalUrl,
        enableRealUserMode: false,
        isBotRequest: false,
        enableBotMode: false,
        requestMethod: req.method,
      });

      const sessionOrHash = getSessionOrHash(req, serverCookies);

      if (isAppLinksSubdomainRequest) {
        proxyConfig = resolveAppLinksProxyConfig(hostname);
      } else {
        try {
          proxyConfig = await resolveWebProxyConfig(req, traceId, hostname);
        } catch (error) {
          logPoaProxyStatus({
            req,
            environment: proxyConfig?.environment ?? 'production',
            attributes: {
              is_api_proxy: isApiProxy,
              error: 'web-proxy-config-failed',
              details: error instanceof Error ? error.message : String(error),
              status_code: 401,
            },
          });
          enhanceTransaction({statusCode: 401});

          return res.status(401).send('UNAUTHORIZED');
        }
      }

      // If the user is not logged in, we do not proxy the request
      // and let it be handled by the reactServerMiddleware, which will show the login screen
      // on in case of the App Links environment, we throw a structured error that will be caught in the catch block, and we render a specific error UI for the App Links environment, since in the App Links environment, the user should never see the login screen, and if they are not authenticated
      if (!isApiProxy && checkLoginNeeded(sessionOrHash)) {
        if (proxyConfig.isAppLinksSubdomainRequest) {
          throw new PayOrderAppAppLinksProxyError({
            message:
              'TDR-FE: Access to Pay Order App is denied due to missing or invalid session in App Links environment',
            status: 403,
          });
        }
        return next();
      }

      const {targetUrl, method, headers, body} =
        await getProxyRequestProperties({
          req,
          tadaridaRestUrl: proxyConfig.tadaridaRestUrl,
          sessionOrHash,
        });

      let upstream = await fetch(targetUrl, {
        method,
        headers,
        body: body ? new Uint8Array(body) : undefined,
        redirect: 'manual',
      });

      /**
       * Force login or show error page in case of 401 Unauthorized or 412 Precondition Failed responses from the upstream.
       * In case of API Proxy, we simply pass the response back to the client (the POA), as the client should handle these responses accordingly.
       */
      if (!isApiProxy) {
        if (upstream.status === 401) {
          if (proxyConfig.isAppLinksSubdomainRequest) {
            throw new PayOrderAppAppLinksProxyError({
              message:
                'TDR-BE: User is not authenticated in App Links environment',
              status: upstream.status,
            });
          }
          return next();
        } else if (upstream.status === 412) {
          // If the response is a 412 Precondition Failed, we assume that the session is valid, but the
          // POA data needed in the BE is not set in redis or is expired.
          // to set the POA data again, we call the POA entry point with the AY Session Token again, which will refresh the data in redis and allow the user to continue.

          // In case of the App, this should not happen, as the app would always call
          // GetPOAURL before opening the Pay Order App WebView, and the POA data would be available in the BE.

          if (proxyConfig.isAppLinksSubdomainRequest) {
            throw new PayOrderAppAppLinksProxyError({
              message:
                'TDR-BE: Precondition Failed - Missing POA data in App Links environment. This indicates that PoA is accessed without calling GetPOAURL first, or that the POA data has expired in the backend.',
              status: upstream.status,
            });
          } else {
            const webProxyConfig = proxyConfig as Extract<
              ProxyConfig,
              {isAppLinksSubdomainRequest: false}
            >;
            const grpcConfig = getGrpcConfig(
              webProxyConfig.staticConfig.i18nConfig.locale,
              webProxyConfig.staticConfig.hostConfig.device,
              [],
            );

            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const setPOAEntryPoint = useStandaloneGrpcRequest(
              {
                method: PaymentService_GetPOAURLLazy,
                params: {
                  session: {
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-non-null-asserted-optional-chain
                    authToken: sessionOrHash?.sessionId!,
                  },
                  orderId: req.query.orderId as unknown as number,
                },
              },
              {
                hostConfig: {
                  hostname: webProxyConfig.staticConfig.hostConfig.tadaridaUrl,
                  legacyHostname:
                    webProxyConfig.staticConfig.hostConfig.gripsUrl,
                },
                grpcConfig,
              },
            );
            await setPOAEntryPoint();

            // retry the upstream request after refreshing the POA data in the BE
            upstream = await fetch(targetUrl, {
              method,
              headers,
              body: body ? new Uint8Array(body) : undefined,
              redirect: 'manual',
            });
          }
        }
      }
      res.status(upstream.status);

      upstream.headers.forEach((value, key) => {
        if (!HOP_BY_HOP_HEADERS.has(key.toLowerCase())) {
          res.setHeader(key, value);
        }
      });

      // In case of the App Links environment, we need to set the AY Session Token cookie in the response, so that the Pay Order App can access it and use it for authentication in subsequent requests.
      if (proxyConfig.isAppLinksSubdomainRequest) {
        const sessionIdHeader = getHashToSessionIdHeader(upstream);

        if (sessionIdHeader) {
          res.cookie(
            TADARIDA_USER_PERSIST_KEY,
            JSON.stringify({token: sessionIdHeader, hash: sessionOrHash?.hash}),
            {httpOnly: true},
          );
        }
      }

      const responseBody = Buffer.from(await upstream.arrayBuffer());

      // DD Log
      logPoaProxyStatus({
        req,
        environment: proxyConfig.environment,
        attributes: {
          is_api_proxy: isApiProxy,
          status_code: upstream.status,
          ...(upstream.status >= 400 && {error: 'upstream-error'}),
        },
      });

      // DD Trace
      enhanceTransaction({statusCode: upstream.status});

      res.send(responseBody);
    } catch (error) {
      Log.error('@aboutyou/logic-components', 'Error in Pay Order App Proxy', {
        error,
      });

      logPoaProxyStatus({
        req,
        environment: proxyConfig?.environment ?? 'production',
        attributes: {
          is_api_proxy: isApiProxy,
          error:
            error instanceof PayOrderAppAppLinksProxyError
              ? 'app-links-proxy-error'
              : 'proxy-middleware-error',
          details: error instanceof Error ? error.message : String(error),
          status_code: 'status' in error ? error.status : error.statusCode,
        },
      });

      if (isApiProxy) {
        return res.status(500).json({
          error: error.message || 'Unexpected error in Pay Order App API Proxy',
        });
      }

      if (error instanceof PayOrderAppAppLinksProxyError) {
        const device = req.query.device as DEVICE_PARAM_TYPE | undefined;
        const orderId = req.query.orderId as string | undefined;

        return res
          .status(error.status)
          .header('x-reason', error.message)
          .send(
            renderToString(
              <AppLinksErrorUI orderId={orderId} device={device} />,
            ),
          );
      }

      next(error);
    }
  };

export const createPayOrderAppWebProxy = () => createPayOrderProxy(false);
createPayOrderAppWebProxy.PUBLIC_ROUTE = PAY_ORDER_WEB_ROUTE;

export const createPayOrderAppApiProxy = () => createPayOrderProxy(true);
createPayOrderAppApiProxy.PUBLIC_ROUTE = PAY_ORDER_API_ROUTE;
